/**
 * LeviathanBrain - Elite AI brain for LEVIATHAN-class worms
 *
 * LEVIATHAN is a legendary AI that challenges even skilled players.
 * It uses a sophisticated state machine with patience, prediction,
 * and tactical awareness far beyond standard AI.
 *
 * State Machine:
 * - PATROL: Smooth movement through MID/RIM zones, scanning for opportunities
 * - STALK: Following at safe distance, waiting for mistakes
 * - ENGAGE: Active pursuit with intercept prediction
 * - AMBUSH: Waiting at strategic points (feasts, death drops)
 * - VULTURE: Lurking at death sites, trapping greedy players
 * - COIL: Executing encirclement traps
 */

import {
  AIState,
  AIConfig,
  DEFAULT_AI_CONFIG,
  ZONE_BOUNDARIES,
  PowerupType,
} from '@wooorms/shared';

import type {
  ClientAIWormData,
  ClientAIFoodData,
  ClientAIPowerUpData,
  ClientAIGameState,
  AIDecision,
} from './AIBrainClient';

// =============================================================================
// LEVIATHAN-SPECIFIC TYPES AND ENUMS
// =============================================================================

/**
 * LEVIATHAN State Machine - More sophisticated than standard AI states
 */
export enum LeviathanState {
  /** Default: Smooth patrol through MID/RIM zones */
  PATROL = 'PATROL',
  /** Tracking a viable target at safe distance */
  STALK = 'STALK',
  /** Active pursuit when kill is likely */
  ENGAGE = 'ENGAGE',
  /** Waiting at strategic points (feasts, death drops) */
  AMBUSH = 'AMBUSH',
  /** Lurking at death sites */
  VULTURE = 'VULTURE',
  /** Executing encirclement trap */
  COIL = 'COIL',
  /** Pursuing a high-value powerup */
  HUNT_POWERUP = 'HUNT_POWERUP',
  /** Emergency: flee from edge */
  FLEE_EDGE = 'FLEE_EDGE',
  /** Emergency: evade bigger threat */
  EVADE = 'EVADE',
}

/**
 * Threat assessment levels
 */
export type ThreatLevel = 'prey' | 'equal' | 'dangerous' | 'lethal';

/**
 * Target opportunity assessment
 */
export interface TargetOpportunity {
  target: ClientAIWormData;
  distance: number;
  isNearEdge: boolean;
  isEating: boolean;
  isFighting: boolean;
  isPredictable: boolean;
  opportunityScore: number;
}

/**
 * Death site information for vulture behavior
 */
export interface DeathSite {
  x: number;
  y: number;
  foodCount: number;
  totalValue: number;
  timestamp: number;
}

/**
 * Extended worm data for LEVIATHAN AI
 */
export interface LeviathanWormData extends ClientAIWormData {
  leviathanState: LeviathanState;
  stalkTarget: string | null;
  stalkTimer: number;
  patienceTimer: number;
  coilPhase: number;
  coilTarget: string | null;
  ambushPosition: { x: number; y: number } | null;
  lastKnownDeathSites: DeathSite[];
  powerupTarget: string | null;
}

// =============================================================================
// LEVIATHAN CONFIGURATION
// =============================================================================

/**
 * LEVIATHAN-specific configuration
 */
export const LEVIATHAN_CONFIG = {
  // Stalking behavior - TIGHTER stalking for quicker strikes
  STALK_DISTANCE_MIN: 200,      // Was 300 - get closer before striking
  STALK_DISTANCE_MAX: 400,      // Was 500 - don't let prey escape
  STALK_DISTANCE_OPTIMAL: 300,  // Was 400 - optimal strike range
  MAX_PATIENCE_SECONDS: 15,     // Was 30 - less patience, more action
  PATIENCE_DECAY_RATE: 0.5,     // Was 1.0 - slower decay, more persistent

  // Engagement thresholds - MORE AGGRESSIVE
  ENGAGE_DISTANCE: 350,         // Was 250 - engage from further away
  SPRINT_CUTOFF_DISTANCE: 300,  // Was 200 - sprint from further
  INTERCEPT_LOOKAHEAD: 2.0,     // Was 1.5 - predict further ahead

  // Coiling behavior - START COILING SOONER
  COIL_START_DISTANCE: 400,     // Was 300 - start coil trap earlier
  COIL_TIGHTEN_RATE: 0.03,      // Was 0.02 - tighten faster
  COIL_MIN_SCORE_RATIO: 1.3,    // Was 1.5 - coil even when only 30% bigger

  // Threat assessment
  DANGEROUS_SCORE_RATIO: 1.5,   // Was 1.3 - brave against larger worms
  LETHAL_SCORE_RATIO: 2.5,      // Was 2.0 - only flee from much larger

  // Zone preferences
  PATROL_PREFER_MID: 0.6, // 60% chance to prefer MID zone
  PATROL_PREFER_RIM: 0.3, // 30% chance to prefer RIM zone

  // Ambush/Vulture
  DEATH_SITE_MEMORY_MS: 30000, // Remember death sites for 30 seconds
  AMBUSH_WAIT_TIME: 10000, // Wait up to 10 seconds at ambush point
  VULTURE_DISTANCE: 150, // Stay this far from death food center

  // Edge trapping - LARGER TRAP ZONE
  EDGE_TRAP_DISTANCE: 400,      // Was 300 - trap from further out

  // Think rate (LEVIATHAN thinks more often)
  THINK_INTERVAL_MS: 16,        // Was 33 - 60Hz thinking for faster reactions

  // Powerup hunting
  POWERUP_HUNT_RADIUS: 600, // Distance to consider powerups
  POWERUP_PRIORITY_THRESHOLD: 2.0, // Minimum priority to actively hunt
  POWERUP_SPRINT_DISTANCE: 300, // Sprint for powerups within this distance

  // Aggressive hunting parameters
  CHASE_ABANDON_DISTANCE: 800,   // Give up chase if target escapes this far
  OPPORTUNISTIC_ENGAGE_SCORE: 0.4, // Engage immediately if opportunity score > this
  STALK_TO_ENGAGE_DISTANCE: 250,   // Switch from stalk to engage when this close

  // CRAWL MODE - Use slow speed for SHARP TURNS
  CRAWL_CUTOFF_ANGLE: Math.PI / 3,     // 60 degrees - crawl when need to turn more than this
  CRAWL_COIL_PHASE: 0.8,               // Start crawling in coil phase 3 (final tightening)
  CRAWL_PRECISION_DISTANCE: 100,       // Crawl when very close to target for precision
  CRAWL_EVASION_ANGLE: Math.PI / 2,    // Crawl during sharp evasive maneuvers

  // FOOD HUNTING - LEVIATHAN actively hunts food to grow
  FOOD_HUNT_RADIUS: 400,               // Search radius for food
  FOOD_HUNT_PRIORITY_RADIUS: 150,      // Within this radius, always grab food
  DEATH_FOOD_PRIORITY_MULTIPLIER: 3,   // Death food is 3x more valuable
  MIN_FOOD_SCORE_TO_DETOUR: 0.3,       // Minimum score to detour from current path
} as const;

/**
 * LEVIATHAN powerup priority - as a legendary hunter, prioritizes combat powerups
 */
function getLeviathanPowerupPriority(powerupType: string): number {
  switch (powerupType) {
    case PowerupType.SPEED:
      // Speed is ESSENTIAL for a hunter - highest priority
      return 3.5;

    case PowerupType.AGILITY:
      // Agility helps cut off prey and escape traps
      return 3.0;

    case PowerupType.MULTIPLIER_6X:
      // 6x is always worth grabbing
      return 3.0;

    case PowerupType.MULTIPLIER_4X:
      // 4x is good for growing stronger
      return 2.0;

    case PowerupType.MULTIPLIER_2X:
      // 2x is nice but not worth interrupting a hunt
      return 1.2;

    case PowerupType.MAGNET:
      // Magnet is for collectors, not hunters
      return 0.8;

    default:
      return 1.0;
  }
}

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

function dist(x1: number, y1: number, x2: number, y2: number): number {
  return Math.hypot(x2 - x1, y2 - y1);
}

function distFromCenter(x: number, y: number): number {
  return Math.hypot(x, y);
}

function normalizeAngle(a: number): number {
  while (a > Math.PI) a -= Math.PI * 2;
  while (a < -Math.PI) a += Math.PI * 2;
  return a;
}

function randomInRange(min: number, max: number): number {
  return Math.random() * (max - min) + min;
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * Math.max(0, Math.min(1, t));
}

/**
 * Predict where target will be in the future
 * SMART INTERCEPT: Calculate optimal intercept point based on both worm speeds
 */
function predictInterceptPoint(
  target: ClientAIWormData,
  lookAheadTime: number = LEVIATHAN_CONFIG.INTERCEPT_LOOKAHEAD,
  hunterX?: number,
  hunterY?: number,
  hunterBoosting?: boolean
): { x: number; y: number; interceptTime: number } {
  const targetSpeed = target.boosting ? 260 : 130; // Actual game speeds
  const hunterSpeed = hunterBoosting ? 260 : 130;

  // Simple prediction if no hunter position
  if (hunterX === undefined || hunterY === undefined) {
    const futureX = target.headX + Math.cos(target.angle) * targetSpeed * lookAheadTime;
    const futureY = target.headY + Math.sin(target.angle) * targetSpeed * lookAheadTime;
    return { x: futureX, y: futureY, interceptTime: lookAheadTime };
  }

  // SMART INTERCEPT: Calculate where hunter should go to intercept
  // Use iterative approximation to find optimal intercept time
  let bestTime = lookAheadTime;
  let bestPoint = { x: target.headX, y: target.headY };

  for (let t = 0.2; t <= lookAheadTime; t += 0.2) {
    // Where will target be at time t?
    const futureX = target.headX + Math.cos(target.angle) * targetSpeed * t;
    const futureY = target.headY + Math.sin(target.angle) * targetSpeed * t;

    // How long would it take hunter to get there?
    const distToIntercept = Math.hypot(futureX - hunterX, futureY - hunterY);
    const timeToReach = distToIntercept / hunterSpeed;

    // If hunter can reach before target passes, this is a valid intercept
    if (timeToReach <= t + 0.3) { // 0.3s margin for turning
      bestTime = t;
      bestPoint = { x: futureX, y: futureY };
      break; // Take earliest intercept point
    }
  }

  return { ...bestPoint, interceptTime: bestTime };
}

/**
 * Calculate optimal cut-off angle to intercept target
 */
function calculateCutOffAngle(
  hunterX: number,
  hunterY: number,
  target: ClientAIWormData,
  hunterBoosting: boolean
): number {
  const intercept = predictInterceptPoint(target, 2.0, hunterX, hunterY, hunterBoosting);

  // Aim for intercept point, not current position
  return Math.atan2(intercept.y - hunterY, intercept.x - hunterX);
}

/**
 * Check if target is near world edge (edge trap opportunity)
 */
function canEdgeTrap(
  targetX: number,
  targetY: number,
  worldRadius: number
): boolean {
  const distToEdge = worldRadius - distFromCenter(targetX, targetY);
  return distToEdge < LEVIATHAN_CONFIG.EDGE_TRAP_DISTANCE;
}

/**
 * Determine which zone a position is in
 */
function getZone(x: number, y: number, worldRadius: number): 'CORE' | 'MID' | 'RIM' {
  const normalizedDist = distFromCenter(x, y) / worldRadius;
  if (normalizedDist <= ZONE_BOUNDARIES.CORE_END) return 'CORE';
  if (normalizedDist <= ZONE_BOUNDARIES.MID_END) return 'MID';
  return 'RIM';
}

/**
 * Assess threat level of another worm
 * LEVIATHAN is aggressive - treats equals as viable targets
 */
function assessThreat(myScore: number, otherScore: number): ThreatLevel {
  const ratio = otherScore / myScore;
  if (ratio >= LEVIATHAN_CONFIG.LETHAL_SCORE_RATIO) return 'lethal';
  if (ratio >= LEVIATHAN_CONFIG.DANGEROUS_SCORE_RATIO) return 'dangerous';
  // Leviathan considers anything not dangerous as potential prey
  // Even "equal" worms can be outmaneuvered
  if (ratio >= 1.1) return 'equal';
  return 'prey';
}

/**
 * Check if a point is safe (no nearby worm bodies)
 */
function isPointSafe(
  x: number,
  y: number,
  worms: ClientAIWormData[],
  myId: string,
  safeRadius: number
): boolean {
  for (const worm of worms) {
    if (!worm.alive || worm.id === myId) continue;
    const startIdx = worm.id === myId ? 5 : 0;
    for (let i = startIdx; i < worm.segments.length; i++) {
      const seg = worm.segments[i];
      if (!seg) continue;
      if (dist(x, y, seg.x, seg.y) < safeRadius + seg.radius) {
        return false;
      }
    }
  }
  return true;
}

/**
 * Scan path for collisions
 */
function scanPath(
  startX: number,
  startY: number,
  angle: number,
  distance: number,
  worms: ClientAIWormData[],
  myId: string,
  dangerRadius: number
): boolean {
  const steps = 5;
  const stepSize = distance / steps;
  for (let i = 1; i <= steps; i++) {
    const checkX = startX + Math.cos(angle) * stepSize * i;
    const checkY = startY + Math.sin(angle) * stepSize * i;
    if (!isPointSafe(checkX, checkY, worms, myId, dangerRadius)) {
      return false;
    }
  }
  return true;
}

/**
 * Find death food clusters (potential death sites)
 */
function findDeathFoodCluster(
  foods: ClientAIFoodData[],
  x: number,
  y: number,
  radius: number
): { count: number; totalValue: number; centerX: number; centerY: number } {
  let count = 0;
  let totalValue = 0;
  let sumX = 0;
  let sumY = 0;

  for (const food of foods) {
    if (!food.fromDeath) continue;
    const d = dist(x, y, food.x, food.y);
    if (d < radius) {
      count++;
      totalValue += food.value;
      sumX += food.x;
      sumY += food.y;
    }
  }

  return {
    count,
    totalValue,
    centerX: count > 0 ? sumX / count : x,
    centerY: count > 0 ? sumY / count : y,
  };
}

/**
 * Get food density in an area
 */
function getFoodDensity(
  x: number,
  y: number,
  foods: ClientAIFoodData[],
  radius: number
): number {
  let density = 0;
  for (const food of foods) {
    const d = dist(x, y, food.x, food.y);
    if (d < radius) {
      const weight = food.fromDeath ? 3 : 1;
      density += food.value * weight * (1 - d / radius);
    }
  }
  return density;
}

// =============================================================================
// LEVIATHAN BRAIN CLASS
// =============================================================================

/**
 * Movement history entry for tracking worm patterns
 */
interface MovementHistoryEntry {
  x: number;
  y: number;
  angle: number;
  timestamp: number;
}

/**
 * LeviathanBrain - Elite AI brain for legendary LEVIATHAN worms
 * Uses sophisticated state machine with patience and prediction
 *
 * ADVANCED FEATURES:
 * - Movement history tracking for pattern prediction
 * - Trap detection (detects if being encircled)
 * - Crawl mode for precision turns
 * - Smart intercept prediction
 */
export class LeviathanBrain {
  // Instance state for tracking across ticks
  private static _stalkTimers: Map<string, number> = new Map();
  private static _patienceTimers: Map<string, number> = new Map();
  private static _stalkTargets: Map<string, string> = new Map();
  private static _coilTargets: Map<string, string> = new Map();
  private static _coilPhases: Map<string, number> = new Map();
  private static _ambushPositions: Map<string, { x: number; y: number; time: number }> = new Map();
  private static _deathSites: Map<string, DeathSite[]> = new Map();
  private static _currentStates: Map<string, LeviathanState> = new Map();
  private static _lastThinkTime: Map<string, number> = new Map();
  private static _powerupTargets: Map<string, string> = new Map();

  // ADVANCED: Trap escape tracking
  private static _trapEscapeAngle: Map<string, number> = new Map();

  // ADVANCED: Movement history for pattern prediction
  private static _targetMovementHistory: Map<string, MovementHistoryEntry[]> = new Map();
  private static readonly MOVEMENT_HISTORY_LENGTH = 20; // Track last 20 positions
  private static readonly MOVEMENT_HISTORY_INTERVAL_MS = 100; // Record every 100ms

  // Track when we last recorded movement for each target
  private static _lastMovementRecord: Map<string, number> = new Map();

  /**
   * ADVANCED: Track target movement for pattern prediction
   * Records position history to predict where they'll go
   */
  private static _recordTargetMovement(target: ClientAIWormData, now: number): void {
    const lastRecord = this._lastMovementRecord.get(target.id) ?? 0;
    if (now - lastRecord < this.MOVEMENT_HISTORY_INTERVAL_MS) return;

    this._lastMovementRecord.set(target.id, now);

    let history = this._targetMovementHistory.get(target.id);
    if (!history) {
      history = [];
      this._targetMovementHistory.set(target.id, history);
    }

    history.push({
      x: target.headX,
      y: target.headY,
      angle: target.angle,
      timestamp: now,
    });

    // Keep only recent history
    if (history.length > this.MOVEMENT_HISTORY_LENGTH) {
      history.shift();
    }
  }

  /**
   * ADVANCED: Analyze movement pattern to predict future behavior
   * Returns: turn tendency (-1 = left, 0 = straight, +1 = right), and consistency (0-1)
   */
  private static _analyzeMovementPattern(targetId: string): { turnTendency: number; consistency: number; avgSpeed: number } {
    const history = this._targetMovementHistory.get(targetId);
    if (!history || history.length < 5) {
      return { turnTendency: 0, consistency: 0, avgSpeed: 130 }; // Default
    }

    let totalAngleChange = 0;
    let totalDistance = 0;
    let consistentTurns = 0;
    let lastTurnDir = 0;

    for (let i = 1; i < history.length; i++) {
      const prev = history[i - 1]!;
      const curr = history[i]!;

      // Calculate angle change
      const angleChange = normalizeAngle(curr.angle - prev.angle);
      totalAngleChange += angleChange;

      // Calculate distance moved
      const dx = curr.x - prev.x;
      const dy = curr.y - prev.y;
      totalDistance += Math.hypot(dx, dy);

      // Check turn consistency
      const turnDir = Math.sign(angleChange);
      if (turnDir !== 0 && turnDir === lastTurnDir) {
        consistentTurns++;
      }
      lastTurnDir = turnDir || lastTurnDir;
    }

    const avgAngleChange = totalAngleChange / (history.length - 1);
    const timeSpan = (history[history.length - 1]!.timestamp - history[0]!.timestamp) / 1000;
    const avgSpeed = timeSpan > 0 ? totalDistance / timeSpan : 130;

    // Calculate consistency (0-1 where 1 = very consistent turning pattern)
    const consistency = Math.min(1, consistentTurns / (history.length - 2));

    return {
      turnTendency: Math.sign(avgAngleChange) * Math.min(1, Math.abs(avgAngleChange) * 5),
      consistency,
      avgSpeed,
    };
  }

  /**
   * ADVANCED: Detect if we're being trapped (enemy body segments surrounding us)
   * Returns threat level 0-1 and best escape direction
   */
  private static _detectTrap(
    worm: ClientAIWormData,
    allWorms: ClientAIWormData[]
  ): { threatLevel: number; escapeAngle: number | null } {
    const head = { x: worm.headX, y: worm.headY };
    const checkRadius = 200;
    const numDirections = 8;
    const blockedDirections: boolean[] = [];
    let blockedCount = 0;

    // Check 8 directions for enemy segments
    for (let i = 0; i < numDirections; i++) {
      const checkAngle = (i / numDirections) * Math.PI * 2;
      const checkX = head.x + Math.cos(checkAngle) * checkRadius;
      const checkY = head.y + Math.sin(checkAngle) * checkRadius;

      let blocked = false;
      for (const other of allWorms) {
        if (!other.alive || other.id === worm.id) continue;

        // Check if path to this direction is blocked by segments
        for (const seg of other.segments.slice(5)) { // Skip head segments
          if (!seg) continue;
          const segDist = dist(checkX, checkY, seg.x, seg.y);
          if (segDist < seg.radius * 2 + worm.radius) {
            blocked = true;
            break;
          }
        }
        if (blocked) break;
      }

      blockedDirections.push(blocked);
      if (blocked) blockedCount++;
    }

    // Calculate threat level
    const threatLevel = blockedCount / numDirections;

    // Find best escape direction (longest chain of open directions)
    let bestEscapeAngle: number | null = null;
    if (threatLevel > 0.3 && blockedCount < numDirections) {
      let longestOpen = 0;
      let longestStart = -1;

      // Find longest consecutive open segment (wrap around)
      let currentOpen = 0;
      let currentStart = -1;
      for (let i = 0; i < numDirections * 2; i++) {
        const idx = i % numDirections;
        if (!blockedDirections[idx]) {
          if (currentOpen === 0) currentStart = idx;
          currentOpen++;
          if (currentOpen > longestOpen) {
            longestOpen = currentOpen;
            longestStart = currentStart;
          }
        } else {
          currentOpen = 0;
        }
      }

      if (longestStart >= 0) {
        // Escape through middle of open segment
        const middleIdx = (longestStart + Math.floor(longestOpen / 2)) % numDirections;
        bestEscapeAngle = (middleIdx / numDirections) * Math.PI * 2;
      }
    }

    return { threatLevel, escapeAngle: bestEscapeAngle };
  }

  /**
   * ADVANCED: Find high-value food targets actively
   * LEVIATHAN should always be growing, not just hunting
   */
  private static _findBestFoodTarget(
    head: { x: number; y: number },
    foods: ClientAIFoodData[],
    maxRadius: number,
    preferDeathFood: boolean = true
  ): { food: ClientAIFoodData; score: number } | null {
    let best: { food: ClientAIFoodData; score: number } | null = null;
    let bestScore = 0;

    for (const food of foods) {
      const d = dist(head.x, head.y, food.x, food.y);
      if (d > maxRadius) continue;

      // Score = value / distance, with bonuses
      let score = food.value / (d + 20);

      // Death food is more valuable (concentrated from kills)
      if (food.fromDeath && preferDeathFood) {
        score *= 3;
      }

      // Bonus for high-value individual items
      if (food.value >= 5) score *= 1.5;
      if (food.value >= 10) score *= 1.5;

      // Small penalty for very distant food
      if (d > 300) score *= 0.8;

      if (score > bestScore) {
        bestScore = score;
        best = { food, score };
      }
    }

    return best;
  }

  /**
   * Main think method - called each tick for LEVIATHAN worms
   */
  static think(
    worm: ClientAIWormData,
    gameState: ClientAIGameState,
    dt: number,
    _config: AIConfig = DEFAULT_AI_CONFIG
  ): AIDecision {
    const now = Date.now();
    const lastThink = this._lastThinkTime.get(worm.id) ?? 0;

    // LEVIATHAN thinks more frequently than regular AI
    if (now - lastThink < LEVIATHAN_CONFIG.THINK_INTERVAL_MS) {
      // Return current direction, no change
      return {
        targetAngle: worm.angle,
        sprinting: worm.boosting,
        newState: AIState.HUNT_FOOD, // Placeholder
      };
    }
    this._lastThinkTime.set(worm.id, now);

    const head = { x: worm.headX, y: worm.headY };
    const allWorms = this._buildAllWormsArray(worm, gameState);
    const worldRadius = gameState.worldRadius;

    // Initialize state if needed
    if (!this._currentStates.has(worm.id)) {
      this._currentStates.set(worm.id, LeviathanState.PATROL);
      this._patienceTimers.set(worm.id, LEVIATHAN_CONFIG.MAX_PATIENCE_SECONDS);
      this._deathSites.set(worm.id, []);
    }

    // Update death site memory
    this._updateDeathSites(worm.id, gameState.foods, now);

    // Get current LEVIATHAN state
    let currentState = this._currentStates.get(worm.id)!;

    // ADVANCED: Track movement of all nearby worms for pattern analysis
    for (const other of allWorms) {
      if (!other.alive || other.id === worm.id) continue;
      const d = dist(head.x, head.y, other.headX, other.headY);
      if (d < 800) {
        this._recordTargetMovement(other, now);
      }
    }

    // Priority checks - edge danger and lethal threats override everything
    const edgeDist = distFromCenter(head.x, head.y);
    const edgeDanger = edgeDist > worldRadius - 400;

    if (edgeDanger) {
      currentState = LeviathanState.FLEE_EDGE;
    } else {
      // ADVANCED: Check if we're being trapped (encircled by enemy segments)
      const trapCheck = this._detectTrap(worm, allWorms);
      if (trapCheck.threatLevel > 0.5 && trapCheck.escapeAngle !== null) {
        // We're being encircled! Emergency escape!
        currentState = LeviathanState.EVADE;
        // Store escape angle for use in _executeEvade
        this._trapEscapeAngle.set(worm.id, trapCheck.escapeAngle);
      } else {
        this._trapEscapeAngle.delete(worm.id);

        // Check for lethal threats
        const lethalThreat = this._findLethalThreat(worm, allWorms);
        if (lethalThreat) {
          currentState = LeviathanState.EVADE;
        } else {
          // State machine transitions
          currentState = this._evaluateStateTransition(
            worm,
            currentState,
            gameState,
            allWorms,
            dt
          );
        }
      }
    }

    this._currentStates.set(worm.id, currentState);

    // Execute current state
    const decision = this._executeState(
      worm,
      currentState,
      gameState,
      allWorms,
      dt
    );

    // Final collision avoidance
    const dangerRadius = worm.radius * 2;
    const pathClear = scanPath(
      head.x,
      head.y,
      decision.targetAngle,
      120,
      allWorms,
      worm.id,
      dangerRadius
    );

    if (!pathClear && currentState !== LeviathanState.FLEE_EDGE) {
      const safeAngle = this._findSafeDirection(
        head,
        decision.targetAngle,
        allWorms,
        worm.id,
        dangerRadius,
        worldRadius
      );
      return {
        targetAngle: safeAngle,
        sprinting: false,
        newState: AIState.EVADE,
      };
    }

    // Log state for debugging
    if (process.env.NODE_ENV === 'development') {
      // console.log(`[LEVIATHAN ${worm.id}] State: ${currentState}, Angle: ${decision.targetAngle.toFixed(2)}, Sprint: ${decision.sprinting}`);
    }

    return decision;
  }

  /**
   * Build combined worms array
   */
  private static _buildAllWormsArray(
    _currentWorm: ClientAIWormData,
    gameState: ClientAIGameState
  ): ClientAIWormData[] {
    const allWorms: ClientAIWormData[] = [...gameState.worms];
    if (gameState.playerWorm && gameState.playerWorm.alive) {
      allWorms.push(gameState.playerWorm);
    }
    return allWorms;
  }

  /**
   * Update death site memory
   */
  private static _updateDeathSites(
    wormId: string,
    foods: ClientAIFoodData[],
    now: number
  ): void {
    const sites = this._deathSites.get(wormId) ?? [];

    // Remove old sites
    const validSites = sites.filter(
      (s) => now - s.timestamp < LEVIATHAN_CONFIG.DEATH_SITE_MEMORY_MS
    );

    // Find new death food clusters
    const processed = new Set<string>();
    for (const food of foods) {
      if (!food.fromDeath || processed.has(food.id)) continue;

      const cluster = findDeathFoodCluster(foods, food.x, food.y, 200);
      if (cluster.count >= 5 && cluster.totalValue >= 10) {
        // Check if this is a new site
        const existingSite = validSites.find(
          (s) => dist(s.x, s.y, cluster.centerX, cluster.centerY) < 300
        );
        if (!existingSite) {
          validSites.push({
            x: cluster.centerX,
            y: cluster.centerY,
            foodCount: cluster.count,
            totalValue: cluster.totalValue,
            timestamp: now,
          });
        }
      }

      // Mark foods in this cluster as processed
      for (const f of foods) {
        if (f.fromDeath && dist(food.x, food.y, f.x, f.y) < 200) {
          processed.add(f.id);
        }
      }
    }

    this._deathSites.set(wormId, validSites);
  }

  /**
   * Find lethal threats (worms much bigger than us facing us)
   */
  private static _findLethalThreat(
    worm: ClientAIWormData,
    allWorms: ClientAIWormData[]
  ): ClientAIWormData | null {
    const head = { x: worm.headX, y: worm.headY };

    for (const other of allWorms) {
      if (!other.alive || other.id === worm.id) continue;

      const threat = assessThreat(worm.score, other.score);
      if (threat !== 'lethal' && threat !== 'dangerous') continue;

      const otherHead = { x: other.headX, y: other.headY };
      const d = dist(head.x, head.y, otherHead.x, otherHead.y);

      if (d < 400) {
        // Check if they're facing us
        const angleToUs = Math.atan2(head.y - otherHead.y, head.x - otherHead.x);
        if (Math.abs(normalizeAngle(other.angle - angleToUs)) < Math.PI / 2) {
          return other;
        }
      }
    }

    return null;
  }

  /**
   * Evaluate state transitions based on game situation
   */
  private static _evaluateStateTransition(
    worm: ClientAIWormData,
    currentState: LeviathanState,
    gameState: ClientAIGameState,
    allWorms: ClientAIWormData[],
    dt: number
  ): LeviathanState {
    const head = { x: worm.headX, y: worm.headY };
    const worldRadius = gameState.worldRadius;

    // Find viable targets
    const opportunity = this._findBestOpportunity(worm, allWorms, worldRadius);
    const deathSites = this._deathSites.get(worm.id) ?? [];
    const nearbyDeathSite = deathSites.find(
      (s) => dist(head.x, head.y, s.x, s.y) < 800
    );

    switch (currentState) {
      case LeviathanState.PATROL: {
        // AGGRESSIVE: Check for viable targets FIRST - hunting is priority
        // Very low threshold - Leviathan always wants to hunt
        if (opportunity && opportunity.opportunityScore > 0.1) {
          // If opportunity is excellent, skip stalk and go straight to engage
          if (opportunity.opportunityScore > LEVIATHAN_CONFIG.OPPORTUNISTIC_ENGAGE_SCORE) {
            this._stalkTargets.set(worm.id, opportunity.target.id);
            return LeviathanState.ENGAGE;
          }
          // Otherwise, start stalking
          this._stalkTargets.set(worm.id, opportunity.target.id);
          this._stalkTimers.set(worm.id, 0);
          this._patienceTimers.set(worm.id, LEVIATHAN_CONFIG.MAX_PATIENCE_SECONDS);
          return LeviathanState.STALK;
        }

        // Check for high-priority powerups (speed, agility for hunting)
        const powerupResult = this._findBestPowerup(
          head,
          gameState.powerUps,
          LEVIATHAN_CONFIG.POWERUP_HUNT_RADIUS
        );
        if (powerupResult && powerupResult.priority >= LEVIATHAN_CONFIG.POWERUP_PRIORITY_THRESHOLD) {
          this._powerupTargets.set(worm.id, powerupResult.powerup.id);
          return LeviathanState.HUNT_POWERUP;
        }

        // Check for vulture opportunity
        if (nearbyDeathSite && nearbyDeathSite.totalValue > 20) {
          return LeviathanState.VULTURE;
        }

        // Check for ambush opportunity at high-value food
        const highValueFood = this._findHighValueFoodCluster(gameState.foods, head);
        if (highValueFood && highValueFood.value > 30) {
          this._ambushPositions.set(worm.id, {
            x: highValueFood.x,
            y: highValueFood.y,
            time: Date.now(),
          });
          return LeviathanState.AMBUSH;
        }

        return LeviathanState.PATROL;
      }

      case LeviathanState.STALK: {
        const targetId = this._stalkTargets.get(worm.id);
        const target = allWorms.find((w) => w.id === targetId && w.alive);

        if (!target) {
          // Target lost, return to patrol
          return LeviathanState.PATROL;
        }

        // Update patience (decays slower now)
        const patience = (this._patienceTimers.get(worm.id) ?? 0) - dt * LEVIATHAN_CONFIG.PATIENCE_DECAY_RATE;
        this._patienceTimers.set(worm.id, patience);

        if (patience <= 0) {
          // Ran out of patience, disengage
          return LeviathanState.PATROL;
        }

        const targetHead = { x: target.headX, y: target.headY };
        const targetDist = dist(head.x, head.y, targetHead.x, targetHead.y);

        // AGGRESSIVE: Engage immediately if close enough
        if (targetDist < LEVIATHAN_CONFIG.STALK_TO_ENGAGE_DISTANCE) {
          return LeviathanState.ENGAGE;
        }

        // Check if target made a mistake - ENGAGE IMMEDIATELY
        const madeMistake = this._targetMadeMistake(worm, target, gameState, allWorms);
        if (madeMistake) {
          // Mistake = immediate engage, regardless of distance (if within chase range)
          return LeviathanState.ENGAGE;
        }

        // Check for coil opportunity - start coiling from further away now
        if (
          worm.score > target.score * LEVIATHAN_CONFIG.COIL_MIN_SCORE_RATIO &&
          targetDist < LEVIATHAN_CONFIG.COIL_START_DISTANCE &&
          canEdgeTrap(targetHead.x, targetHead.y, worldRadius)
        ) {
          this._coilTargets.set(worm.id, target.id);
          this._coilPhases.set(worm.id, 0);
          return LeviathanState.COIL;
        }

        // OPPORTUNISTIC: If we have a really good shot, take it
        const opportunity = this._findBestOpportunity(worm, allWorms, worldRadius);
        if (opportunity && opportunity.target.id === target.id &&
            opportunity.opportunityScore > LEVIATHAN_CONFIG.OPPORTUNISTIC_ENGAGE_SCORE) {
          return LeviathanState.ENGAGE;
        }

        return LeviathanState.STALK;
      }

      case LeviathanState.ENGAGE: {
        const targetId = this._stalkTargets.get(worm.id);
        const target = allWorms.find((w) => w.id === targetId && w.alive);

        if (!target) {
          return LeviathanState.PATROL;
        }

        const targetHead = { x: target.headX, y: target.headY };
        const targetDist = dist(head.x, head.y, targetHead.x, targetHead.y);

        // PERSISTENT CHASE - only disengage if target escapes VERY far
        if (targetDist > LEVIATHAN_CONFIG.CHASE_ABANDON_DISTANCE) {
          return LeviathanState.PATROL;
        }

        // If target gets far but still in range, check if still worth chasing
        if (targetDist > 500) {
          const threat = assessThreat(worm.score, target.score);
          // Stop chasing if they became dangerous while we were chasing
          if (threat === 'dangerous' || threat === 'lethal') {
            return LeviathanState.PATROL;
          }
          // If they're just running and we're not gaining, maybe switch to stalk
          if (target.boosting && !worm.boosting) {
            return LeviathanState.STALK;
          }
        }

        // Transition to coil if conditions are right
        if (
          worm.score > target.score * LEVIATHAN_CONFIG.COIL_MIN_SCORE_RATIO &&
          targetDist < LEVIATHAN_CONFIG.COIL_START_DISTANCE
        ) {
          this._coilTargets.set(worm.id, target.id);
          this._coilPhases.set(worm.id, 0);
          return LeviathanState.COIL;
        }

        // If target is near edge, try to coil even without size advantage
        if (canEdgeTrap(targetHead.x, targetHead.y, worldRadius) && targetDist < 300) {
          this._coilTargets.set(worm.id, target.id);
          this._coilPhases.set(worm.id, 0);
          return LeviathanState.COIL;
        }

        return LeviathanState.ENGAGE;
      }

      case LeviathanState.AMBUSH: {
        const ambushPos = this._ambushPositions.get(worm.id);
        if (!ambushPos) return LeviathanState.PATROL;

        const elapsed = Date.now() - ambushPos.time;
        if (elapsed > LEVIATHAN_CONFIG.AMBUSH_WAIT_TIME) {
          return LeviathanState.PATROL;
        }

        // Check if prey is approaching
        for (const other of allWorms) {
          if (!other.alive || other.id === worm.id) continue;
          if (assessThreat(worm.score, other.score) !== 'prey') continue;

          const otherDist = dist(ambushPos.x, ambushPos.y, other.headX, other.headY);
          if (otherDist < 200) {
            // Prey is approaching the bait - engage!
            this._stalkTargets.set(worm.id, other.id);
            return LeviathanState.ENGAGE;
          }
        }

        return LeviathanState.AMBUSH;
      }

      case LeviathanState.VULTURE: {
        if (!nearbyDeathSite) {
          return LeviathanState.PATROL;
        }

        // Check if other worms are rushing to the food
        for (const other of allWorms) {
          if (!other.alive || other.id === worm.id) continue;
          if (assessThreat(worm.score, other.score) !== 'prey') continue;

          const otherDist = dist(
            nearbyDeathSite.x,
            nearbyDeathSite.y,
            other.headX,
            other.headY
          );
          if (otherDist < 250) {
            // Prey is rushing for food - intercept!
            this._stalkTargets.set(worm.id, other.id);
            return LeviathanState.ENGAGE;
          }
        }

        return LeviathanState.VULTURE;
      }

      case LeviathanState.COIL: {
        const targetId = this._coilTargets.get(worm.id);
        const target = allWorms.find((w) => w.id === targetId && w.alive);

        if (!target) {
          return LeviathanState.PATROL;
        }

        // Continue coiling
        const phase = (this._coilPhases.get(worm.id) ?? 0) + LEVIATHAN_CONFIG.COIL_TIGHTEN_RATE;
        this._coilPhases.set(worm.id, phase);

        // If coil is complete (phase > 1) or target escaped, check result
        const targetDist = dist(head.x, head.y, target.headX, target.headY);
        if (targetDist > 500 || phase > 2) {
          return LeviathanState.PATROL;
        }

        return LeviathanState.COIL;
      }

      case LeviathanState.HUNT_POWERUP: {
        const targetPowerupId = this._powerupTargets.get(worm.id);
        const targetPowerup = gameState.powerUps.find((p) => p.id === targetPowerupId);

        if (!targetPowerup) {
          // Powerup was collected or despawned, return to patrol
          this._powerupTargets.delete(worm.id);
          return LeviathanState.PATROL;
        }

        const powerupDist = dist(head.x, head.y, targetPowerup.x, targetPowerup.y);

        // If powerup is too far now (we may have been pushed away), re-evaluate
        if (powerupDist > LEVIATHAN_CONFIG.POWERUP_HUNT_RADIUS * 1.2) {
          this._powerupTargets.delete(worm.id);
          return LeviathanState.PATROL;
        }

        // Check if a high-value prey target appeared while hunting powerup
        // LEVIATHAN prioritizes kills over powerups if the opportunity is good
        if (opportunity && opportunity.opportunityScore > 0.6) {
          this._powerupTargets.delete(worm.id);
          this._stalkTargets.set(worm.id, opportunity.target.id);
          return LeviathanState.ENGAGE;
        }

        return LeviathanState.HUNT_POWERUP;
      }

      default:
        return LeviathanState.PATROL;
    }
  }

  /**
   * Find the best target opportunity
   * SMART TARGETING: Evaluates vulnerability, position, and kill potential
   */
  private static _findBestOpportunity(
    worm: ClientAIWormData,
    allWorms: ClientAIWormData[],
    worldRadius: number
  ): TargetOpportunity | null {
    const head = { x: worm.headX, y: worm.headY };
    let bestOpportunity: TargetOpportunity | null = null;
    let bestScore = 0;

    for (const other of allWorms) {
      if (!other.alive || other.id === worm.id) continue;

      const threat = assessThreat(worm.score, other.score);
      // LEVIATHAN is aggressive - targets both 'prey' and 'equal' worms
      // Only avoids 'dangerous' and 'lethal' threats
      if (threat === 'dangerous' || threat === 'lethal') continue;

      const otherHead = { x: other.headX, y: other.headY };
      const distance = dist(head.x, head.y, otherHead.x, otherHead.y);

      // Extended hunt range for Leviathan
      if (distance > 1000) continue;

      const isNearEdge = canEdgeTrap(otherHead.x, otherHead.y, worldRadius);
      const isFighting = this._isInFight(other, allWorms);
      const isPredictable = this._isPredictableMovement(other);

      // Check if target is moving toward us (easy intercept)
      const angleToUs = Math.atan2(head.y - otherHead.y, head.x - otherHead.x);
      const movingTowardUs = Math.abs(normalizeAngle(other.angle - angleToUs)) < Math.PI / 3;

      // Check if we can intercept (we're ahead of their path)
      const intercept = predictInterceptPoint(other, 2.0, head.x, head.y, false);
      const canIntercept = intercept.interceptTime < 1.8;

      // Calculate opportunity score - START HIGHER, BE MORE AGGRESSIVE
      let score = 0.3; // Lower base score but bigger bonuses

      // Distance factor (closer = MUCH higher score)
      // Sharp falloff - strongly prefer close targets
      if (distance < 200) {
        score += 0.5; // Very close = high priority
      } else if (distance < 400) {
        score += 0.35;
      } else if (distance < 600) {
        score += 0.2;
      } else {
        score += 0.1 * (1 - (distance - 600) / 400);
      }

      // Size advantage (bigger difference = higher score)
      const sizeRatio = worm.score / Math.max(other.score, 1);
      if (sizeRatio >= 2) {
        score += 0.3; // Easy prey
      } else if (sizeRatio >= 1.5) {
        score += 0.2;
      } else if (sizeRatio >= 1.2) {
        score += 0.1;
      }
      // Slightly smaller targets are still viable
      if (threat === 'equal') {
        score += 0.05; // Small bonus for challenging fight
      }

      // Situational bonuses - HIGHER VALUES
      if (isNearEdge) score += 0.35;  // Was 0.2 - edge traps are VERY valuable
      if (isFighting) score += 0.4;   // Was 0.25 - distracted targets are prime
      if (isPredictable) score += 0.15; // Was 0.1
      if (movingTowardUs) score += 0.25; // NEW: easy intercept
      if (canIntercept) score += 0.2;    // NEW: we can get to them

      // Player bonus (prefer hunting players over AI) - HIGHER
      if (!other.isAI) score += 0.25; // Was 0.15 - players are the real targets

      // Penalty for targets moving away at high speed
      if (other.boosting && !movingTowardUs) {
        score -= 0.15; // Harder to catch
      }

      // Bonus for low-score targets (easy kills)
      if (other.score < 20) {
        score += 0.1; // Quick snack
      }

      if (score > bestScore) {
        bestScore = score;
        bestOpportunity = {
          target: other,
          distance,
          isNearEdge,
          isEating: false,
          isFighting,
          isPredictable,
          opportunityScore: score,
        };
      }
    }

    return bestOpportunity;
  }

  /**
   * Check if worm is in a fight with another worm
   */
  private static _isInFight(
    worm: ClientAIWormData,
    allWorms: ClientAIWormData[]
  ): boolean {
    const head = { x: worm.headX, y: worm.headY };

    for (const other of allWorms) {
      if (!other.alive || other.id === worm.id) continue;

      const otherHead = { x: other.headX, y: other.headY };
      const distance = dist(head.x, head.y, otherHead.x, otherHead.y);

      // If two worms are very close and facing each other, they're fighting
      if (distance < 150) {
        const angleToOther = Math.atan2(
          otherHead.y - head.y,
          otherHead.x - head.x
        );
        const otherAngleToUs = Math.atan2(
          head.y - otherHead.y,
          head.x - otherHead.x
        );

        const facingOther =
          Math.abs(normalizeAngle(worm.angle - angleToOther)) < Math.PI / 3;
        const otherFacingUs =
          Math.abs(normalizeAngle(other.angle - otherAngleToUs)) < Math.PI / 3;

        if (facingOther && otherFacingUs) {
          return true;
        }
      }
    }

    return false;
  }

  /**
   * Check if worm has predictable movement (going straight)
   */
  private static _isPredictableMovement(_worm: ClientAIWormData): boolean {
    // Simple heuristic: if not boosting, likely more predictable
    // In a full implementation, we'd track angle history
    return !_worm.boosting;
  }

  /**
   * Check if target made a mistake (opportunity to engage)
   */
  private static _targetMadeMistake(
    worm: ClientAIWormData,
    target: ClientAIWormData,
    gameState: ClientAIGameState,
    allWorms: ClientAIWormData[]
  ): boolean {
    const targetHead = { x: target.headX, y: target.headY };

    // Near edge
    if (canEdgeTrap(targetHead.x, targetHead.y, gameState.worldRadius)) {
      return true;
    }

    // Started eating (would need to track this)
    // In fight with someone else
    if (this._isInFight(target, allWorms.filter((w) => w.id !== worm.id))) {
      return true;
    }

    // Moving predictably while we're close
    if (this._isPredictableMovement(target)) {
      const distance = dist(worm.headX, worm.headY, targetHead.x, targetHead.y);
      if (distance < 300) {
        return true;
      }
    }

    return false;
  }

  /**
   * Find high-value food cluster for ambush
   */
  private static _findHighValueFoodCluster(
    foods: ClientAIFoodData[],
    head: { x: number; y: number }
  ): { x: number; y: number; value: number } | null {
    let bestCluster: { x: number; y: number; value: number } | null = null;
    let bestValue = 0;

    // Sample several positions
    for (const food of foods.slice(0, 50)) {
      const density = getFoodDensity(food.x, food.y, foods, 150);
      const distance = dist(head.x, head.y, food.x, food.y);

      // Prefer moderate distance (not too close, not too far)
      const distanceBonus = distance > 200 && distance < 600 ? 1.2 : 1;

      if (density * distanceBonus > bestValue) {
        bestValue = density * distanceBonus;
        bestCluster = { x: food.x, y: food.y, value: density };
      }
    }

    return bestCluster;
  }

  /**
   * Execute the current state and return decision
   */
  private static _executeState(
    worm: ClientAIWormData,
    state: LeviathanState,
    gameState: ClientAIGameState,
    allWorms: ClientAIWormData[],
    _dt: number
  ): AIDecision {
    const head = { x: worm.headX, y: worm.headY };

    switch (state) {
      case LeviathanState.FLEE_EDGE:
        return this._executeFleeEdge(head, worm.score);

      case LeviathanState.EVADE:
        return this._executeEvade(worm, allWorms, gameState.worldRadius);

      case LeviathanState.PATROL:
        return this._executePatrol(worm, gameState, allWorms);

      case LeviathanState.STALK:
        return this._executeStalk(worm, allWorms, gameState.worldRadius);

      case LeviathanState.ENGAGE:
        return this._executeEngage(worm, allWorms);

      case LeviathanState.AMBUSH:
        return this._executeAmbush(worm, gameState);

      case LeviathanState.VULTURE:
        return this._executeVulture(worm, gameState, allWorms);

      case LeviathanState.COIL:
        return this._executeCoil(worm, allWorms);

      case LeviathanState.HUNT_POWERUP:
        return this._executeHuntPowerup(worm, gameState);

      default:
        return this._executePatrol(worm, gameState, allWorms);
    }
  }

  // =========================================================================
  // STATE EXECUTION METHODS
  // =========================================================================

  private static _executeFleeEdge(
    head: { x: number; y: number },
    score: number
  ): AIDecision {
    const targetAngle = Math.atan2(-head.y, -head.x);
    return {
      targetAngle,
      sprinting: score > 50,
      newState: AIState.FLEE_EDGE,
    };
  }

  private static _executeEvade(
    worm: ClientAIWormData,
    allWorms: ClientAIWormData[],
    worldRadius: number
  ): AIDecision {
    const head = { x: worm.headX, y: worm.headY };
    const threat = this._findLethalThreat(worm, allWorms);

    let targetAngle = worm.angle + Math.PI;
    let shouldSprint = worm.score > 30;
    let shouldCrawl = false;

    // ADVANCED: Check if we have a trap escape angle (being encircled)
    const trapEscapeAngle = this._trapEscapeAngle.get(worm.id);
    if (trapEscapeAngle !== undefined) {
      // Use the calculated escape angle from trap detection
      targetAngle = trapEscapeAngle;
      const turnNeeded = Math.abs(normalizeAngle(targetAngle - worm.angle));
      // If sharp turn needed, crawl for precision then sprint out
      if (turnNeeded > LEVIATHAN_CONFIG.CRAWL_EVASION_ANGLE) {
        shouldCrawl = true;
        shouldSprint = false;
      } else {
        shouldSprint = worm.score > 20; // Sprint to escape the trap!
      }
      return {
        targetAngle,
        sprinting: shouldSprint,
        crawling: shouldCrawl,
        newState: AIState.EVADE,
      };
    }

    if (threat) {
      const threatHead = { x: threat.headX, y: threat.headY };
      const baseEscape = Math.atan2(head.y - threatHead.y, head.x - threatHead.x);
      const threatDist = dist(head.x, head.y, threatHead.x, threatHead.y);

      if (threatDist < 200) {
        // Very close - sharp perpendicular turn
        // CRAWL for the sharp turn, then sprint!
        targetAngle = baseEscape + (Math.random() > 0.5 ? Math.PI / 2 : -Math.PI / 2);

        // If we need a VERY sharp turn (>90 degrees), crawl for it
        const turnNeeded = Math.abs(normalizeAngle(targetAngle - worm.angle));
        if (turnNeeded > LEVIATHAN_CONFIG.CRAWL_EVASION_ANGLE) {
          // Sharp evasive maneuver - crawl to make the turn, then we'll sprint after
          shouldCrawl = true;
          shouldSprint = false;
        }
      } else {
        // Factor in edge avoidance
        const escapePoint = {
          x: head.x + Math.cos(baseEscape) * 300,
          y: head.y + Math.sin(baseEscape) * 300,
        };
        const escapeEdgeDist = worldRadius - distFromCenter(escapePoint.x, escapePoint.y);

        if (escapeEdgeDist < 200) {
          // Would escape toward edge - angle toward center
          const toCenter = Math.atan2(-head.y, -head.x);
          targetAngle = normalizeAngle((baseEscape + toCenter) / 2);
        } else {
          targetAngle = baseEscape + randomInRange(-0.3, 0.3);
        }
      }
    }

    return {
      targetAngle,
      sprinting: shouldSprint,
      crawling: shouldCrawl,
      newState: AIState.EVADE,
    };
  }

  private static _executePatrol(
    worm: ClientAIWormData,
    gameState: ClientAIGameState,
    _allWorms: ClientAIWormData[]
  ): AIDecision {
    const head = { x: worm.headX, y: worm.headY };
    const worldRadius = gameState.worldRadius;
    const currentZone = getZone(head.x, head.y, worldRadius);

    // LEVIATHAN's patrol is actually HUNTING - move toward likely player locations
    let targetAngle = worm.angle;
    let shouldSprint = false;

    // Priority 1: If there's a player, patrol toward their general area
    // (but not so close as to trigger STALK - stay at patrol distance)
    if (gameState.playerWorm && gameState.playerWorm.alive) {
      const playerDist = dist(head.x, head.y, gameState.playerWorm.headX, gameState.playerWorm.headY);

      if (playerDist > 600 && playerDist < 1500) {
        // Patrol toward player area - close the distance strategically
        const angleToPlayer = Math.atan2(
          gameState.playerWorm.headY - head.y,
          gameState.playerWorm.headX - head.x
        );
        // Add some randomness so we don't beeline directly
        targetAngle = angleToPlayer + randomInRange(-Math.PI / 4, Math.PI / 4);
      }
    }

    // Priority 2: Zone-based hunting behavior
    if (Math.random() < 0.15) { // More frequent direction changes
      if (currentZone === 'CORE') {
        // CORE is boring - move outward toward MID/RIM where the action is
        const outwardAngle = Math.atan2(head.y, head.x);
        targetAngle = outwardAngle + randomInRange(-Math.PI / 4, Math.PI / 4);
        shouldSprint = worm.score > 30; // Sprint out of core
      } else if (currentZone === 'RIM') {
        // RIM is good hunting ground but avoid edge
        const edgeDist = worldRadius - distFromCenter(head.x, head.y);
        if (edgeDist < 400) {
          const inwardAngle = Math.atan2(-head.y, -head.x);
          targetAngle = inwardAngle + randomInRange(-Math.PI / 4, Math.PI / 4);
        } else {
          // Cruise the rim looking for prey near edge
          const tangentAngle = Math.atan2(head.y, head.x) + Math.PI / 2;
          targetAngle = tangentAngle + randomInRange(-Math.PI / 6, Math.PI / 6);
        }
      } else {
        // MID zone - optimal hunting ground
        // Weave back and forth to cover area
        targetAngle = worm.angle + randomInRange(-Math.PI / 3, Math.PI / 3);
      }
    }

    // Priority 3: ACTIVELY hunt food - LEVIATHAN doesn't just passively collect!
    // Use the advanced food targeting to find the best food in range
    const foodTarget = this._findBestFoodTarget(head, gameState.foods, LEVIATHAN_CONFIG.FOOD_HUNT_RADIUS, true);
    if (foodTarget) {
      const foodAngle = Math.atan2(foodTarget.food.y - head.y, foodTarget.food.x - head.x);
      const foodDist = dist(head.x, head.y, foodTarget.food.x, foodTarget.food.y);
      const angleDiff = Math.abs(normalizeAngle(foodAngle - targetAngle));

      // High-priority food (death food nearby) - always go for it
      if (foodDist < LEVIATHAN_CONFIG.FOOD_HUNT_PRIORITY_RADIUS) {
        targetAngle = foodAngle;
        // Sprint for death food
        if (foodTarget.food.fromDeath && worm.score > 30) {
          shouldSprint = true;
        }
      }
      // Good food score - detour if not too far off path
      else if (foodTarget.score > LEVIATHAN_CONFIG.MIN_FOOD_SCORE_TO_DETOUR && angleDiff < Math.PI / 2) {
        targetAngle = foodAngle;
      }
      // Death food clusters - always worth going for
      else if (foodTarget.food.fromDeath && foodTarget.food.value >= 5) {
        targetAngle = foodAngle;
      }
    }

    // Priority 4: Grab powerups if nearby
    const nearbyPowerup = this._findNearbyPowerup(head, gameState.powerUps, 350);
    if (nearbyPowerup) {
      targetAngle = Math.atan2(nearbyPowerup.y - head.y, nearbyPowerup.x - head.x);
      // Sprint for good powerups
      const priority = getLeviathanPowerupPriority(nearbyPowerup.type);
      if (priority >= 2.5) {
        shouldSprint = worm.score > 40;
      }
    }

    // Small random wobble for natural movement
    targetAngle = normalizeAngle(targetAngle + randomInRange(-0.1, 0.1));

    return {
      targetAngle,
      sprinting: shouldSprint,
      newState: AIState.HUNT_FOOD,
    };
  }

  private static _executeStalk(
    worm: ClientAIWormData,
    allWorms: ClientAIWormData[],
    worldRadius: number
  ): AIDecision {
    const head = { x: worm.headX, y: worm.headY };
    const targetId = this._stalkTargets.get(worm.id);
    const target = allWorms.find((w) => w.id === targetId && w.alive);

    if (!target) {
      return this._executePatrol(worm, { worms: allWorms, playerWorm: null, foods: [], powerUps: [], worldRadius }, allWorms);
    }

    const targetHead = { x: target.headX, y: target.headY };
    const targetDist = dist(head.x, head.y, targetHead.x, targetHead.y);

    // SMART STALKING: Position ahead of target's path, not just follow
    let targetAngle: number;

    // Predict where target will be in 2 seconds
    const futureTarget = predictInterceptPoint(target, 2.0);

    // Calculate ideal stalking position: ahead and to the side of target's path
    const targetDirection = target.angle;
    const perpendicularOffset = Math.PI / 3; // 60 degrees to the side

    // Choose the side that's closer to the world edge (better for trapping)
    const leftSideX = futureTarget.x + Math.cos(targetDirection + perpendicularOffset) * LEVIATHAN_CONFIG.STALK_DISTANCE_OPTIMAL;
    const leftSideY = futureTarget.y + Math.sin(targetDirection + perpendicularOffset) * LEVIATHAN_CONFIG.STALK_DISTANCE_OPTIMAL;
    const rightSideX = futureTarget.x + Math.cos(targetDirection - perpendicularOffset) * LEVIATHAN_CONFIG.STALK_DISTANCE_OPTIMAL;
    const rightSideY = futureTarget.y + Math.sin(targetDirection - perpendicularOffset) * LEVIATHAN_CONFIG.STALK_DISTANCE_OPTIMAL;

    const leftEdgeDist = worldRadius - distFromCenter(leftSideX, leftSideY);
    const rightEdgeDist = worldRadius - distFromCenter(rightSideX, rightSideY);

    // Prefer the side that pushes target toward edge
    let idealX: number, idealY: number;
    if (leftEdgeDist < rightEdgeDist && leftEdgeDist > 200) {
      // Left side is closer to edge but safe
      idealX = leftSideX;
      idealY = leftSideY;
    } else if (rightEdgeDist > 200) {
      idealX = rightSideX;
      idealY = rightSideY;
    } else {
      // Both too close to edge, just follow target direction
      idealX = futureTarget.x;
      idealY = futureTarget.y;
    }

    if (targetDist < LEVIATHAN_CONFIG.STALK_DISTANCE_MIN) {
      // Too close - back off perpendicular to their movement, staying in ambush position
      const backoffDir = targetDirection + Math.PI / 2 * (Math.random() > 0.5 ? 1 : -1);
      targetAngle = backoffDir;
    } else if (targetDist > LEVIATHAN_CONFIG.STALK_DISTANCE_MAX) {
      // Too far - move toward ideal position
      targetAngle = Math.atan2(idealY - head.y, idealX - head.x);
    } else {
      // Optimal range - position ahead of target
      // Blend between following and positioning
      const angleToIdeal = Math.atan2(idealY - head.y, idealX - head.x);
      const angleToTarget = Math.atan2(targetHead.y - head.y, targetHead.x - head.x);
      targetAngle = normalizeAngle((angleToIdeal + angleToTarget) / 2);
    }

    // Light sprint if target is moving fast and we're falling behind
    const shouldSprint = target.boosting && targetDist > LEVIATHAN_CONFIG.STALK_DISTANCE_MAX - 50;

    return {
      targetAngle,
      sprinting: shouldSprint,
      newState: AIState.HUNT_PLAYER,
    };
  }

  private static _executeEngage(
    worm: ClientAIWormData,
    allWorms: ClientAIWormData[]
  ): AIDecision {
    const head = { x: worm.headX, y: worm.headY };
    const targetId = this._stalkTargets.get(worm.id);
    const target = allWorms.find((w) => w.id === targetId && w.alive);

    if (!target) {
      return {
        targetAngle: worm.angle,
        sprinting: false,
        crawling: false,
        newState: AIState.HUNT_FOOD,
      };
    }

    const targetHead = { x: target.headX, y: target.headY };
    const targetDist = dist(head.x, head.y, targetHead.x, targetHead.y);

    // ADVANCED: Analyze target's movement patterns for better prediction
    const movementPattern = this._analyzeMovementPattern(target.id);

    // Decide if we should sprint FIRST (affects intercept calculation)
    const canSprint = worm.score > 20; // Lower threshold - Leviathan is aggressive
    const wantToSprint = targetDist < LEVIATHAN_CONFIG.SPRINT_CUTOFF_DISTANCE ||
                         (targetDist < 400 && target.boosting); // Match their sprint
    let shouldSprint = canSprint && wantToSprint;
    let shouldCrawl = false;

    // SMART INTERCEPT - use our position, sprint status, and movement pattern
    let interceptLookahead = LEVIATHAN_CONFIG.INTERCEPT_LOOKAHEAD;
    // If target has consistent turning pattern, predict further ahead
    if (movementPattern.consistency > 0.6) {
      interceptLookahead *= 1.3;
    }

    const interceptPoint = predictInterceptPoint(
      target,
      interceptLookahead,
      head.x,
      head.y,
      shouldSprint
    );

    // ADVANCED: Adjust intercept based on turn tendency
    // If target consistently turns one direction, aim to that side
    if (Math.abs(movementPattern.turnTendency) > 0.3) {
      const turnOffset = movementPattern.turnTendency * Math.PI / 6; // Up to 30 degrees
      interceptPoint.x += Math.cos(target.angle + Math.PI / 2) * turnOffset * 50;
      interceptPoint.y += Math.sin(target.angle + Math.PI / 2) * turnOffset * 50;
    }

    // Calculate cut-off angle using smart intercept
    let targetAngle: number;

    if (targetDist < 120) {
      // KILL ZONE - very close, precision is KEY
      const angleToTarget = Math.atan2(targetHead.y - head.y, targetHead.x - head.x);
      const angleDiff = normalizeAngle(target.angle - angleToTarget);

      if (Math.abs(angleDiff) < Math.PI / 2) {
        // We're behind them - aggressive side swing to cut off
        // Choose the side that gets us in front faster
        const sideOffset = angleDiff > 0 ? Math.PI / 2.5 : -Math.PI / 2.5;
        targetAngle = angleToTarget + sideOffset;
      } else {
        // We're ahead or to the side - aim at intercept point
        targetAngle = Math.atan2(interceptPoint.y - head.y, interceptPoint.x - head.x);
      }

      // PRECISION CRAWLING: When in kill zone, use crawl for sharp cut-off turns
      const turnNeeded = Math.abs(normalizeAngle(targetAngle - worm.angle));
      if (turnNeeded > LEVIATHAN_CONFIG.CRAWL_CUTOFF_ANGLE) {
        // Sharp turn needed - CRAWL for precision
        shouldCrawl = true;
        shouldSprint = false;
      } else if (targetDist < LEVIATHAN_CONFIG.CRAWL_PRECISION_DISTANCE && turnNeeded > Math.PI / 6) {
        // Very close and need to adjust - crawl for accuracy
        shouldCrawl = true;
        shouldSprint = false;
      }
    } else if (targetDist < 250) {
      // STRIKE ZONE - close enough to commit to intercept
      // Use smart cut-off angle
      targetAngle = calculateCutOffAngle(head.x, head.y, target, shouldSprint);

      // If target is fleeing (moving away from us), be more aggressive
      const fleeAngle = Math.atan2(targetHead.y - head.y, targetHead.x - head.x);
      const targetMovement = normalizeAngle(target.angle - fleeAngle);
      if (Math.abs(targetMovement) > Math.PI * 0.6) {
        // Target is fleeing! Sprint and pursue
        // Aim ahead of where they're going
        targetAngle = target.angle; // Match their direction to catch up
      }

      // Check if sharp cut-off is needed
      const turnNeeded = Math.abs(normalizeAngle(targetAngle - worm.angle));
      if (turnNeeded > LEVIATHAN_CONFIG.CRAWL_CUTOFF_ANGLE && !target.boosting) {
        // Need sharp turn and target isn't fleeing fast - crawl to cut them off
        shouldCrawl = true;
        shouldSprint = false;
      }
    } else {
      // APPROACH - head toward intercept point
      targetAngle = Math.atan2(interceptPoint.y - head.y, interceptPoint.x - head.x);
    }

    // Extra sprint logic: if target is sprinting away, we MUST sprint to keep up (never crawl)
    const targetFleeing = target.boosting && targetDist > 150;
    if (targetFleeing) {
      shouldSprint = canSprint;
      shouldCrawl = false;
    }

    return {
      targetAngle,
      sprinting: shouldSprint,
      crawling: shouldCrawl,
      newState: AIState.HUNT_PLAYER,
    };
  }

  private static _executeAmbush(
    worm: ClientAIWormData,
    gameState: ClientAIGameState
  ): AIDecision {
    const head = { x: worm.headX, y: worm.headY };
    const ambushPos = this._ambushPositions.get(worm.id);

    if (!ambushPos) {
      return this._executePatrol(worm, gameState, gameState.worms);
    }

    const distToAmbush = dist(head.x, head.y, ambushPos.x, ambushPos.y);

    // Move to ambush position
    let targetAngle: number;
    if (distToAmbush > 50) {
      targetAngle = Math.atan2(ambushPos.y - head.y, ambushPos.x - head.x);
    } else {
      // At ambush position - circle slowly
      targetAngle = worm.angle + 0.02;
    }

    // Pick up food while waiting
    const nearbyFood = this._findBestNearbyFood(head, gameState.foods, 100);
    if (nearbyFood) {
      targetAngle = Math.atan2(nearbyFood.y - head.y, nearbyFood.x - head.x);
    }

    return {
      targetAngle,
      sprinting: false,
      newState: AIState.HUNT_FOOD,
    };
  }

  private static _executeVulture(
    worm: ClientAIWormData,
    gameState: ClientAIGameState,
    allWorms: ClientAIWormData[]
  ): AIDecision {
    const head = { x: worm.headX, y: worm.headY };
    const deathSites = this._deathSites.get(worm.id) ?? [];
    const nearestSite = deathSites.reduce<DeathSite | null>((nearest, site) => {
      const d = dist(head.x, head.y, site.x, site.y);
      if (!nearest || d < dist(head.x, head.y, nearest.x, nearest.y)) {
        return site;
      }
      return nearest;
    }, null);

    if (!nearestSite) {
      return this._executePatrol(worm, gameState, allWorms);
    }

    const distToSite = dist(head.x, head.y, nearestSite.x, nearestSite.y);

    // Stay at the outskirts - don't rush in
    let targetAngle: number;
    if (distToSite > LEVIATHAN_CONFIG.VULTURE_DISTANCE + 50) {
      // Move closer
      targetAngle = Math.atan2(nearestSite.y - head.y, nearestSite.x - head.x);
    } else if (distToSite < LEVIATHAN_CONFIG.VULTURE_DISTANCE - 50) {
      // Back off a bit
      targetAngle = Math.atan2(head.y - nearestSite.y, head.x - nearestSite.x);
    } else {
      // Circle around the site
      const angleToSite = Math.atan2(nearestSite.y - head.y, nearestSite.x - head.x);
      targetAngle = angleToSite + Math.PI / 2;
    }

    return {
      targetAngle,
      sprinting: false,
      newState: AIState.HUNT_FOOD,
    };
  }

  private static _executeCoil(
    worm: ClientAIWormData,
    allWorms: ClientAIWormData[]
  ): AIDecision {
    const head = { x: worm.headX, y: worm.headY };
    const targetId = this._coilTargets.get(worm.id);
    const target = allWorms.find((w) => w.id === targetId && w.alive);
    const phase = this._coilPhases.get(worm.id) ?? 0;

    if (!target) {
      return {
        targetAngle: worm.angle,
        sprinting: false,
        crawling: false,
        newState: AIState.HUNT_FOOD,
      };
    }

    const targetHead = { x: target.headX, y: target.headY };
    const targetDist = dist(head.x, head.y, targetHead.x, targetHead.y);

    // SMART COILING: Predict where target wants to escape and block it

    // Phase 0-0.5: Get ahead of target's path (SPRINT)
    // Phase 0.5-1.0: Circle to cut off (SPRINT/NORMAL)
    // Phase 1.0+: Tighten the noose (CRAWL for precision turns!)

    // Where does target want to go?
    const targetEscapeDir = target.angle;

    // Calculate position ahead of target
    const futureTarget = predictInterceptPoint(target, 1.0);

    let targetAngle: number;
    let shouldSprint = false;
    let shouldCrawl = false;

    if (phase < 0.5) {
      // PHASE 1: Get ahead of target
      // Position ourselves in front of where they're going
      const blockDist = 150;
      const blockX = futureTarget.x + Math.cos(targetEscapeDir) * blockDist;
      const blockY = futureTarget.y + Math.sin(targetEscapeDir) * blockDist;
      targetAngle = Math.atan2(blockY - head.y, blockX - head.x);
      shouldSprint = true; // Sprint to get into position
    } else if (phase < 1.0) {
      // PHASE 2: Circle around to cut off all escape routes
      const coilRadius = lerp(200, 100, (phase - 0.5) * 2);
      const coilAngle = Math.atan2(targetHead.y - head.y, targetHead.x - head.x) + Math.PI / 2;

      // Choose the side that blocks their escape
      const leftBlockX = targetHead.x + Math.cos(coilAngle) * coilRadius;
      const leftBlockY = targetHead.y + Math.sin(coilAngle) * coilRadius;
      const rightBlockX = targetHead.x + Math.cos(coilAngle + Math.PI) * coilRadius;
      const rightBlockY = targetHead.y + Math.sin(coilAngle + Math.PI) * coilRadius;

      // Pick the side that's more in their escape path
      const leftBlocking = Math.abs(normalizeAngle(Math.atan2(leftBlockY - targetHead.y, leftBlockX - targetHead.x) - targetEscapeDir));
      const rightBlocking = Math.abs(normalizeAngle(Math.atan2(rightBlockY - targetHead.y, rightBlockX - targetHead.x) - targetEscapeDir));

      if (leftBlocking < rightBlocking) {
        targetAngle = Math.atan2(leftBlockY - head.y, leftBlockX - head.x);
      } else {
        targetAngle = Math.atan2(rightBlockY - head.y, rightBlockX - head.x);
      }
      shouldSprint = targetDist > 150;
    } else {
      // PHASE 3: Tighten - CRAWL for precision turns!
      // This is where crawling shines - tight spiral requires sharp turns
      const directAngle = Math.atan2(targetHead.y - head.y, targetHead.x - head.x);

      // Add spiral motion - tighter spirals need sharper turns
      const spiralOffset = Math.sin(phase * Math.PI * 2) * 0.4; // Increased spiral
      targetAngle = directAngle + spiralOffset;

      // Calculate how sharp the turn is
      const turnNeeded = Math.abs(normalizeAngle(targetAngle - worm.angle));

      // CRAWL for sharp turns in final phase - this is the key!
      // Crawling gives 30% faster turn rate at 50% speed
      if (turnNeeded > LEVIATHAN_CONFIG.CRAWL_CUTOFF_ANGLE || targetDist < LEVIATHAN_CONFIG.CRAWL_PRECISION_DISTANCE) {
        shouldCrawl = true;
        shouldSprint = false;
      } else if (worm.score > 30) {
        shouldSprint = true; // Sprint to close if turn is manageable
      }
    }

    return {
      targetAngle,
      sprinting: shouldSprint,
      crawling: shouldCrawl,
      newState: AIState.HUNT_PLAYER,
    };
  }

  /**
   * HUNT_POWERUP state - actively pursue a high-value powerup
   * LEVIATHAN sprints for speed/agility powerups to enhance hunting ability
   */
  private static _executeHuntPowerup(
    worm: ClientAIWormData,
    gameState: ClientAIGameState
  ): AIDecision {
    const head = { x: worm.headX, y: worm.headY };
    const targetPowerupId = this._powerupTargets.get(worm.id);

    // Find the target powerup
    const targetPowerup = gameState.powerUps.find((p) => p.id === targetPowerupId);

    if (!targetPowerup) {
      // Powerup gone, fall back to patrol behavior
      return {
        targetAngle: worm.angle + randomInRange(-0.2, 0.2),
        sprinting: false,
        newState: AIState.HUNT_FOOD,
      };
    }

    const powerupDist = dist(head.x, head.y, targetPowerup.x, targetPowerup.y);
    const targetAngle = Math.atan2(targetPowerup.y - head.y, targetPowerup.x - head.x);
    const powerupPriority = getLeviathanPowerupPriority(targetPowerup.type);

    // Determine sprint behavior:
    // - Sprint for very high priority powerups (speed, agility, 10x)
    // - Sprint when close to secure the pickup
    // - Only sprint if we have enough score to afford it
    let shouldSprint = false;
    const canSprint = worm.score > 50;

    if (canSprint) {
      if (powerupDist < 150) {
        // Very close - sprint to grab it before someone else
        shouldSprint = true;
      } else if (powerupDist < LEVIATHAN_CONFIG.POWERUP_SPRINT_DISTANCE && powerupPriority >= 3.0) {
        // High-value powerup (speed/agility) - worth sprinting for
        shouldSprint = true;
      } else if (powerupDist < 200 && powerupPriority >= 2.5) {
        // Good powerup, moderate distance - sprint
        shouldSprint = true;
      }
    }

    return {
      targetAngle,
      sprinting: shouldSprint,
      newState: AIState.COLLECT_POWERUP,
    };
  }

  // =========================================================================
  // HELPER METHODS
  // =========================================================================

  /**
   * Find best nearby food
   */
  private static _findBestNearbyFood(
    head: { x: number; y: number },
    foods: ClientAIFoodData[],
    radius: number
  ): ClientAIFoodData | null {
    let best: ClientAIFoodData | null = null;
    let bestScore = 0;

    for (const food of foods) {
      const d = dist(head.x, head.y, food.x, food.y);
      if (d > radius) continue;

      const valueMultiplier = food.fromDeath ? 3 : 1;
      const score = (food.value * valueMultiplier) / (d + 10);

      if (score > bestScore) {
        bestScore = score;
        best = food;
      }
    }

    return best;
  }

  /**
   * Find the best powerup for LEVIATHAN based on priority and distance
   * Returns both the powerup and its calculated score
   */
  private static _findBestPowerup(
    head: { x: number; y: number },
    powerUps: ClientAIPowerUpData[],
    radius: number
  ): { powerup: ClientAIPowerUpData; score: number; priority: number } | null {
    let bestResult: { powerup: ClientAIPowerUpData; score: number; priority: number } | null = null;
    let bestScore = 0;

    for (const pu of powerUps) {
      const d = dist(head.x, head.y, pu.x, pu.y);
      if (d > radius) continue;

      const priority = getLeviathanPowerupPriority(pu.type);

      // Score = priority * 100 / (distance + offset)
      // This means high-priority powerups can be pursued from further away
      const score = priority * 100 / (d + 30);

      if (score > bestScore) {
        bestScore = score;
        bestResult = { powerup: pu, score, priority };
      }
    }

    return bestResult;
  }

  /**
   * Find nearby powerup (simple version for patrol mode)
   */
  private static _findNearbyPowerup(
    head: { x: number; y: number },
    powerUps: ClientAIPowerUpData[],
    radius: number
  ): ClientAIPowerUpData | null {
    const result = this._findBestPowerup(head, powerUps, radius);
    return result?.powerup ?? null;
  }

  /**
   * Find safe direction to move
   */
  private static _findSafeDirection(
    head: { x: number; y: number },
    currentAngle: number,
    worms: ClientAIWormData[],
    myId: string,
    dangerRadius: number,
    worldRadius: number
  ): number {
    const directions = [
      0,
      Math.PI / 6,
      -Math.PI / 6,
      Math.PI / 4,
      -Math.PI / 4,
      Math.PI / 3,
      -Math.PI / 3,
      Math.PI / 2,
      -Math.PI / 2,
    ];

    let bestAngle = currentAngle;
    let bestScore = -Infinity;

    for (const offset of directions) {
      const testAngle = normalizeAngle(currentAngle + offset);
      const testDist = 150;

      if (!scanPath(head.x, head.y, testAngle, testDist, worms, myId, dangerRadius)) {
        continue;
      }

      const testX = head.x + Math.cos(testAngle) * testDist;
      const testY = head.y + Math.sin(testAngle) * testDist;
      const edgeDist = worldRadius - distFromCenter(testX, testY);

      const angleScore = 1 - Math.abs(offset) / Math.PI;
      const edgeScore = Math.min(1, edgeDist / 300);
      const score = angleScore * 0.6 + edgeScore * 0.4;

      if (score > bestScore) {
        bestScore = score;
        bestAngle = testAngle;
      }
    }

    return bestAngle;
  }

  /**
   * Reset state for a worm (call when worm dies or is removed)
   */
  static resetState(wormId: string): void {
    this._currentStates.delete(wormId);
    this._stalkTimers.delete(wormId);
    this._patienceTimers.delete(wormId);
    this._stalkTargets.delete(wormId);
    this._coilTargets.delete(wormId);
    this._coilPhases.delete(wormId);
    this._ambushPositions.delete(wormId);
    this._deathSites.delete(wormId);
    this._lastThinkTime.delete(wormId);
    this._powerupTargets.delete(wormId);
    this._trapEscapeAngle.delete(wormId);
    this._targetMovementHistory.delete(wormId);
    this._lastMovementRecord.delete(wormId);
  }

  /**
   * Check if this brain should handle a worm (based on isLeviathan flag or name)
   */
  static isLeviathanWorm(worm: ClientAIWormData): boolean {
    // Check for explicit leviathan flag if it exists
    // Also check personality type - Leviathan has its own personality
    return (
      (worm as unknown as { isLeviathan?: boolean }).isLeviathan === true ||
      worm.personality?.type === ('Leviathan' as unknown as string)
    );
  }
}
